import React, { useState } from "react";
import { Share2, MessageCircle, Copy, Check, Smartphone } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

// Share the report card: native share sheet with the PNG when available, otherwise WhatsApp / text / copy links.
const ReportCardShare = ({ targetRef, data }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = window.location.href;
  const text = `${data.child.name}'s ${data.year} report card on Teach Your Child Credit: ${data.active_days} active day${data.active_days === 1 ? "" : "s"}, longest streak ${data.longest_streak_weeks} week${data.longest_streak_weeks === 1 ? "" : "s"}, ${data.certificates_total} certificate${data.certificates_total === 1 ? "" : "s"}${data.quiz_average !== null ? `, quiz average ${data.quiz_average}/5` : ""}. ${url}`;

  const nativeShare = async () => {
    setBusy(true);
    try {
      const dataUrl = await toPng(targetRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true });
      const file = new File([await (await fetch(dataUrl)).blob()], `${data.child.name}-${data.year}-report-card.png`, { type: "image/png" });
      const payload = { title: `${data.child.name}'s ${data.year} report card`, text };
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ ...payload, files: [file] });
      else await navigator.share({ ...payload, url });
      toast.success("Shared");
    } catch (e) {
      if (e?.name !== "AbortError") { setOpen(true); }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Summary copied"); setTimeout(() => setCopied(false), 2000); } catch { toast.error("Could not copy"); }
  };

  return (
    <div className="relative">
      <button onClick={() => (navigator.share ? nativeShare() : setOpen((o) => !o))} disabled={busy} className="btn-pill bg-[#63c79a] hover:bg-[#4fb98a] text-white inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="report-card-share"><Share2 size={18} /> {busy ? "Preparing..." : "Share"}</button>
      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-[#c9ecf5] p-2 z-20 bubble-in" data-testid="report-card-share-menu">
          <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold text-[#0c4a6e] hover:bg-[#e9f9fd]" data-testid="report-card-share-whatsapp"><span className="w-8 h-8 rounded-full bg-[#25d366] text-white flex items-center justify-center"><MessageCircle size={16} /></span> WhatsApp</a>
          <a href={`sms:?&body=${encodeURIComponent(text)}`} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold text-[#0c4a6e] hover:bg-[#e9f9fd]" data-testid="report-card-share-sms"><span className="w-8 h-8 rounded-full bg-[#1b8bc9] text-white flex items-center justify-center"><Smartphone size={16} /></span> Text message</a>
          <button onClick={copy} className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-bold text-[#0c4a6e] hover:bg-[#e9f9fd]" data-testid="report-card-share-copy"><span className="w-8 h-8 rounded-full bg-[#7d5fb2] text-white flex items-center justify-center">{copied ? <Check size={16} /> : <Copy size={16} />}</span> {copied ? "Copied" : "Copy summary"}</button>
          <p className="px-3 pt-1 pb-1 text-[11px] text-gray-400">Tip: "Download image" first to attach the picture.</p>
        </div>
      )}
    </div>
  );
};

export default ReportCardShare;
